import { useTranslation as _useTranslation } from 'react-i18next';
import type { UseTranslationOptions } from 'react-i18next';

import { getKeys } from '../../translations/keys';
import { TranslationSchema } from '../../translations/resources';

/**
 * Hook do lokalnych tłumaczeń bez zmiany globalnego języka.
 * Używany np. w podglądzie ankiet gdzie zmiana języka nie powinna wpływać na resztę systemu.
 *
 * @param ns - Namespace tłumaczeń
 * @param localLanguage - Kod języka (np. 'PL', 'EN') - jeśli podany, wszystkie tłumaczenia będą w tym języku
 * @param options - Dodatkowe opcje dla react-i18next
 */
export const useLocalTranslation = <T extends keyof TranslationSchema>(
  ns: T,
  localLanguage?: string,
  options?: UseTranslationOptions<T>,
) => {
  const { t: originalT, i18n } = _useTranslation(ns, options);

  // Mapowanie kodu języka (PL/EN) na pełną nazwę namespace (plProjects/enProjects)
  const getFullLanguage = (langCode: string): string => {
    const langMap: Record<string, string> = {
      PL: 'plProjects',
      EN: 'enProjects',
    };
    return langMap[langCode.toUpperCase()] || langMap.PL;
  };

  // Wrapper funkcji t() który automatycznie dodaje parametr lng
  const t = ((key: string | string[], opts?: unknown) => {
    if (localLanguage) {
      const fullLang = getFullLanguage(localLanguage);
      // Użyj lokalnego języka zamiast globalnego
      return originalT(key, {
        lng: fullLang,
        ...(opts as object),
      });
    }
    // Jeśli nie podano localLanguage, użyj normalnie
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return originalT(key as any, opts as any);
  }) as typeof originalT;

  return {
    t,
    i18n,
    tk: getKeys(ns),
  };
};
